import React, { ComponentPropsWithoutRef } from 'react';

import { Grid } from '@rbx/ui';
import throwError from '@modules/utilities/error';
import MarkdownProvider from './MarkdownProvider';
import useGridContainerStyles from './GridContainer.styles';

type DivProps = ComponentPropsWithoutRef<'div'>;

// Supports auto, 2, 3, and 4 column layouts
type ColumnType = 'auto' | 2 | 3 | 4;
type ColumnValues = {
  xs: 'auto' | 6 | 12;
  sm?: 4 | 6;
  lg?: 3;
};

export interface GridContainerProps extends DivProps {
  numColumns: ColumnType;
}

const numColumnsToValues: Record<ColumnType, ColumnValues> = {
  auto: { xs: 'auto' },
  2: { xs: 12, sm: 6 },
  3: { xs: 6, sm: 4 },
  4: { xs: 6, lg: 3 },
};

const DEFAULT_MEDIA_WIDTH = '100%';

export default function GridContainer({ numColumns, children, ...otherProps }: GridContainerProps) {
  const {
    classes: { root },
  } = useGridContainerStyles();

  if (!Array.isArray(children)) {
    const errorMessage = `Unexpected type of children received for a GridContainer. numColumns: ${numColumns}, children: ${React.Children.count(
      children,
    )}`;
    throwError(errorMessage);
  }

  const { xs, sm, lg } = numColumnsToValues[numColumns];

  return (
    <Grid container spacing={4} className={root} {...otherProps}>
      <MarkdownProvider widths={{ image: DEFAULT_MEDIA_WIDTH, video: DEFAULT_MEDIA_WIDTH }}>
        {Array.isArray(children)
          ? children.map((child, index) => {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <Grid item XSmall={xs} Medium={sm} XLarge={lg} key={index}>
                  {child}
                </Grid>
              );
            })
          : null}
      </MarkdownProvider>
    </Grid>
  );
}
